import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Col,
  FormControl,
  Badge,
} from "react-bootstrap";
import UploadProgress from "../../components/UploadProgress";
import Select from "../../components/ui/AppReactSelect";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DOMPurify from "dompurify";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { AppAsyncSelect as AsyncSelect } from "../../components/ui/AppReactSelect";
import CategorySuggestions from "../../components/catalog/CategorySuggestions";
import { categoryPickerOptions, unitSelectOptions } from "../../utilities/catalogTaxonomy";
import { getSiUnits } from "actions/siUnitActions";
import { uploadImageToCloudinary as sharedUpload } from "../../utilities/imageUpload";
import ImageUploadStyledAlert from "views/Components/ImageUploadStyledAlert";
import { toast } from "react-toastify";

import { createPromoListing } from "../../actions/listingsActions";
import { getUserFromLocalStorage } from "utilities/user";

const PromoForm = ({ onSuccess } = {}) => {
  const { siUnits, loading } = useSelector((state) => state.siUnits);
  const [selectedUnit, setSelectedUnit] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const user = getUserFromLocalStorage();
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [inputValue, setValue] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  // Every category, kept for the suggestions to resolve their picks against.
  const [allCategories, setAllCategories] = useState([]);

  //Return policy
  const [useDefaultPolicy, setUseDefaultPolicy] = useState(true);
  const [customPolicy, setCustomPolicy] = useState("");

  // Alert
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [description, setDescription] = useState("");

  const [postData, setPostData] = useState({
    title: "",
    price: "",
    countInStock: "",
    measurements: "",
    vendorName: "",
    vendorContactNumber: "",
    vendorAddress: "",
    pic: "",
  });

  const [image, setImage] = useState("");

  const dispatch = useDispatch();
  const history = useHistory();

  const clear = () => {
    setPostData({
      title: "",
      price: "",
      countInStock: "",
      measurements: "",
      pic: "",
    }),
      setSelectedCategory([]);
    setDescription("");
    setSelectedCategory([]);
    setSelectedUnit([]);
    setCustomPolicy("");
    setDescription("");
    setImage("");
    setIsUploading(false);
    setUploadDone(false);
    setUploadProgress(0);
    setUploadedFileName("");
  };

  useEffect(() => {
    dispatch(getSiUnits());
  }, []);

  /* The unit picker reads `siUnits` directly through unitSelectOptions(),
     which groups by family and leaves plural duplicates out. */

  const fetchImageToUpload = useCallback(() => {
    return new Promise(async (resolve, reject) => {
      setIsUploading(true);

      try {
        const result = await sharedUpload(image, {
          onProgress: (percent) => setUploadProgress(percent),
        });

        setImage(result.secure_url);
        setIsUploading(false);
        setUploadDone(true);

        // Return an AbortController for compatibility with the cleanup pattern
        const abortController = new AbortController();
        resolve(abortController);
      } catch (err) {
        setIsUploading(false);
        reject(err);
      }
    });
  }, [image]);

  useEffect(() => {
    let active = true;
    let abortControllerPromise;

    if (image?.name) {
      if (active) {
        abortControllerPromise = fetchImageToUpload();
      }
    }
    return () => {
      active = false;
      if (abortControllerPromise) {
        abortControllerPromise.then((abortController) => {
          abortController.abort();
        });
      }
    };
  }, [image?.name, fetchImageToUpload]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (image?.name && !uploadDone) {
      const abortController = await fetchImageToUpload();
      if (abortController && !abortController.signal.aborted) {
        abortController.abort();
      }
    }

    // Sanitize customPolicy text
    const sanitizedCustomPolicy = DOMPurify.sanitize(customPolicy);

    dispatch(
      createPromoListing(
        {
          ...postData,
          pic: image,
          name: user?.result?.name,
          category: selectedCategory,
          unit_id: selectedUnit.value,
          description: description,
          useDefaultPolicy: useDefaultPolicy,
          customPolicy: sanitizedCustomPolicy,
        },
        user?.result?._id
      )
    );
    clear();

    if (onSuccess) {
      onSuccess();
    } else {
      history.push("/admin/shopCards");
    }
  };

  const loadCategory = async (inputValue) => {
    const res = await fetch("/category/get-all");
    const categories = await res.json();
    if (Array.isArray(categories)) setAllCategories(categories);
    // Grouped, "All"/"Random"/inactive left out, aliases and keywords searchable.
    return categoryPickerOptions(Array.isArray(categories) ? categories : [], {
      input: inputValue,
      selected: selectedCategory,
    });
  };

  // handle selection
  const handleInputChange = (value) => {
    setValue(value);
  };

  // handle Client Selection
  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
  };

  // handle Unit Selection
  const handleUnitChange = (value) => {
    setSelectedUnit(value);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Basic validation - the shared utility handles detailed validation
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setSnackbarSeverity("error");
      setSnackbarMessage(
        "Invalid file type. Only JPG, JPEG, and PNG files are allowed."
      );
      setOpenSnackbar(true);
      setUploadedFileName("");
      return;
    }

    // Continue with the upload process
    setImage(file);
    setUploadedFileName(file.name);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  const handleDescriptionChange = (value) => {
    setDescription(value);
  };

  //React Quill modules
  const modules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ size: ["small", false, "large", "huge"] }],
      [{ color: [] }, { background: [] }],
      ["clean"],
    ],
  };

  return (
    <Col md="10">
      <style>{`
        .form-section {
          padding: 15px;
          margin-bottom: 15px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: #fff;
        }
        .field-label {
          font-weight: 600;
          color: #333;
          margin-bottom: 8px;
          display: block;
        }
        .field-required {
          color: #f00;
          margin-left: 3px;
        }
        .field-helper {
          color: #888;
          font-size: 12px;
          margin-top: 5px;
          display: block;
        }
        .section-title {
          font-weight: 600;
          color: #333;
          margin-bottom: 10px;
          padding-bottom: 8px;
          border-bottom: 1px solid #ddd;
        }
        .upload-zone {
          border: 2px dashed #ccc;
          border-radius: 4px;
          padding: 20px;
          text-align: center;
          background: #fafafa;
          cursor: pointer;
        }
        .upload-zone:hover {
          border-color: #999;
          background: #f5f5f5;
        }
      `}</style>
      <Form
        action=""
        id="RegisterValidation"
        autoComplete="off"
        method=""
        onSubmit={handleSubmit}
      >
        <Card>
          <Card.Header>
            <Card.Title as="h4" style={{ color: "#f00" }}>
              Promo Form
            </Card.Title>
            <p className="card-category">Products ~ Create Promo listing.</p>
          </Card.Header>
          <Card.Body className="pt-0">
            {/* Product Name */}
            <Col md={12}>
              <Form.Group>
                <div className="form-section">
                  <label className="field-label">
                    1. Product Name<span className="field-required">*</span>
                  </label>
                  <Form.Control
                    name="title"
                    type="text"
                    value={postData.title}
                    maxLength="40"
                    placeholder="Enter product name"
                    onChange={(e) =>
                      setPostData({
                        ...postData,
                        title: e.target.value,
                      })
                    }
                  ></Form.Control>
                  <small className="field-helper">
                    {postData.title.length}/40 characters
                  </small>
                </div>
                {/* Description */}
                <div className="form-section">
                  <label className="field-label">
                    2. Product Description
                    <span className="field-required">*</span>
                  </label>
                  <ReactQuill
                    value={description}
                    onChange={handleDescriptionChange}
                    modules={modules}
                    formats={[
                      "bold",
                      "italic",
                      "underline",
                      "strike",
                      "blockquote",
                      "list",
                      "bullet",
                      "size",
                      "color",
                      "background",
                    ]}
                    style={{
                      height: "220px",
                      marginBottom: "50px",
                    }}
                    placeholder="Enter product description here..."
                  />
                </div>
                {/* Price */}
                <div className="form-section">
                  <label className="field-label">
                    3. Item Price<span className="field-required">*</span>
                  </label>
                  <Form.Control
                    name="price"
                    type="text"
                    pattern="[0-9]*"
                    value={postData.price}
                    placeholder="Enter price"
                    onChange={(e) =>
                      setPostData({
                        ...postData,
                        price: e.target.value,
                      })
                    }
                  ></Form.Control>
                </div>
                <div className="form-section">
                  <label className="field-label">
                    4. In Stock<span className="field-required">*</span>
                  </label>
                  <Form.Control
                    name="countInStock"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={postData.countInStock}
                    placeholder="Enter quantity"
                    onChange={(e) =>
                      setPostData({
                        ...postData,
                        countInStock: e.target.value,
                      })
                    }
                  ></Form.Control>
                </div>
                <div className="form-section">
                  <label className="field-label">
                    5. Select Category<span className="field-required">*</span>
                  </label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    value={selectedCategory}
                    getOptionLabel={(e) => e.category}
                    getOptionValue={(e) => e._id}
                    loadOptions={loadCategory}
                    onInputChange={handleInputChange}
                    onChange={handleCategoryChange}
                    isClearable={true}
                    isMulti={true}
                    placeholder="Search and select categories..."
                  />
                  <CategorySuggestions
                    name={postData.title}
                    unitId={selectedUnit?.value || null}
                    vendorId={user?.result?._id}
                    categories={allCategories}
                    selected={selectedCategory}
                    onAdd={(category) => setSelectedCategory((prev) => [...(prev || []), category])}
                    onApplyUnit={({ _id, unit, measurements }) => {
                      setSelectedUnit({ value: _id, label: unit, name: unit });
                      setPostData((prev) => ({ ...prev, measurements }));
                    }}
                  />
                </div>
                <div className="form-section">
                  <label className="field-label">
                    6. Measurements<span className="field-required">*</span>
                  </label>
                  <Form.Control
                    name="measurements"
                    type="text"
                    inputMode="numeric"
                    pattern="^\d*(\.\d{0,2})?$"
                    value={postData.measurements}
                    placeholder="Enter measurement value"
                    onChange={(e) =>
                      setPostData({
                        ...postData,
                        measurements: e.target.value,
                      })
                    }
                  ></Form.Control>
                  <small className="field-helper">
                    Numerical value (decimals allowed)
                  </small>
                </div>
                {/* Vendor Information */}
                <div className="form-section">
                  <div className="section-title">
                    Vendor Information (Optional)
                  </div>

                  <Form.Group style={{ marginBottom: "15px" }}>
                    <label className="field-label">7. Vendor Name</label>
                    <Form.Control
                      type="text"
                      value={postData.vendorName}
                      onChange={(e) =>
                        setPostData({ ...postData, vendorName: e.target.value })
                      }
                      placeholder="Enter vendor's name"
                    />
                  </Form.Group>

                  <Form.Group style={{ marginBottom: "15px" }}>
                    <label className="field-label">
                      8. Vendor Primary Contact Number
                    </label>
                    <Form.Control
                      type="text"
                      value={postData.vendorContactNumber}
                      onChange={(e) =>
                        setPostData({
                          ...postData,
                          vendorContactNumber: e.target.value,
                        })
                      }
                      placeholder="Enter vendor's contact number..."
                    />
                  </Form.Group>

                  <Form.Group>
                    <label className="field-label">
                      9. Vendor's Physical Address
                    </label>
                    <Form.Control
                      type="text"
                      value={postData.vendorAddress}
                      onChange={(e) =>
                        setPostData({
                          ...postData,
                          vendorAddress: e.target.value,
                        })
                      }
                      placeholder="Enter vendor's Address..."
                    />
                  </Form.Group>
                </div>
                <div className="form-section">
                  <label className="field-label">
                    10. Measurement Unit
                    <span className="field-required">*</span>
                  </label>
                  <Select
                    options={unitSelectOptions(siUnits, selectedUnit?.value)}
                    onChange={handleUnitChange}
                    value={selectedUnit?.value ? selectedUnit : null}
                    isClearable={true}
                    isSearchable={true}
                    placeholder="Select Measurement Unit..."
                  />
                </div>
              </Form.Group>
              {/* Return Policy */}
              <div className="form-section">
                <Form.Group>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "10px",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="default-return-policy-checkbox"
                      checked={useDefaultPolicy}
                      onChange={(e) => setUseDefaultPolicy(e.target.checked)}
                      style={{ marginRight: "8px" }}
                    />
                    <label htmlFor="default-return-policy-checkbox">
                      Use default return policy
                    </label>
                  </div>
                  {!useDefaultPolicy && (
                    <>
                      <label>
                        Enter Custom Return Policy
                        <span className="star">*</span>
                      </label>
                      <FormControl
                        as="textarea"
                        rows={5}
                        placeholder="Please provide the custom return policy here!"
                        style={{ minHeight: "170px" }}
                        value={customPolicy}
                        onChange={(e) => setCustomPolicy(e.target.value)}
                        required={!useDefaultPolicy}
                      />
                      <small className="field-helper">
                        Please enter a custom return policy.
                      </small>
                    </>
                  )}
                  {useDefaultPolicy && (
                    <small className="field-helper" style={{ color: "green" }}>
                      Default return policy will be used!
                    </small>
                  )}
                </Form.Group>
              </div>
              <div className="form-section upload-zone">
                <label
                  htmlFor="image-upload"
                  style={{
                    display: "inline-block",
                    padding: "6px 12px",
                    cursor: "pointer",
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontWeight: 400,
                    color: "#495057",
                    fontSize: "1rem",
                    lineHeight: "1.5",
                  }}
                >
                  {uploadedFileName || "Upload an image..."}
                </label>
                <input
                  id="image-upload"
                  name="image"
                  type="file"
                  accept=".jpg, .png, .jpeg"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />

                {uploadedFileName && (
                  <p
                    style={{
                      fontSize: "13px",
                      fontStyle: "italic",
                      marginTop: "10px",
                    }}
                  >
                    File Name: {uploadedFileName}
                  </p>
                )}
                {isUploading && (
                  <UploadProgress percent={uploadProgress} label="Uploading file" />
                )}
                {uploadDone && !isUploading && (
                  <p
                    style={{
                      color: "green",
                      fontSize: "13px",
                      fontStyle: "italic",
                      marginTop: "10px",
                    }}
                  >
                    Image Upload Done!
                  </p>
                )}
              </div>
            </Col>
          </Card.Body>

          <Card.Body>
            <Card.Footer className="text-right">
              <Button
                className="btn-fill pull-right"
                variant="danger"
                onClick={clear}
                style={{ marginRight: "5px" }}
              >
                Clear
              </Button>
              <Button
                className="btn-fill pull-right"
                variant="info"
                type="submit"
                disabled={
                  !uploadDone ||
                  selectedCategory.length <= 0 ||
                  selectedUnit?.length === 0
                }
              >
                Submit
              </Button>
              <div className="clearfix"></div>
            </Card.Footer>
          </Card.Body>
        </Card>
      </Form>
    </Col>
  );
};

export default PromoForm;
